import type React from "react";
import { useEffect } from "react";

import { faCircleQuestion, faPhone } from "@fortawesome/pro-regular-svg-icons";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Player } from "@lottiefiles/react-lottie-player";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { Link, useMatch, useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import { CheckmarkIcon, ClockColor } from "assets";
import jsonSmall from "assets/animations/dot-animation-small.json";
import json from "assets/animations/dot-animation.json";
import { useDropInCallContext } from "contexts/DropInCallContext";
import { useDropInCallTimerContext } from "contexts/DropInCallContext/useDropInCallTimerContext";
import { useNotificationsContext } from "contexts/NotificationsContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUISwitch } from "shared/atoms/MUI/MUISwitch";
import { Tooltip } from "shared/atoms/Tooltip";
import { DropInCallFeedbackPopup } from "shared/molecules/DropInCallFeedbackPopup";
import { BLUE, DARK_BLUE } from "theme/colors";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";

import { DisconnectModal } from "./DisconnectedModal";
import { useDropInCallNotification } from "./useDropInCallNotification";

interface Props {
  collapsed: boolean;
}

export const DropInCallPane: React.FC<Props> = ({ collapsed }) => {
  const {
    toggleAvailability,
    available,
    isReady,
    currentSession,
    startReviewing,
    finishReviewing,
    expiryTime,
    errorCode,
    requestStatus,
    askForFeedback,
    setAskForFeedback,
    patientName = "",
  } = useDropInCallContext();
  const { timerTime } = useDropInCallTimerContext();
  const navigate = useNavigate();
  useDropInCallNotification();

  const sessionState = currentSession?.state;
  const patientId = currentSession?.patient_id;
  const match = useMatch(`/patients/${currentSession?.patient_id}/overview`);

  useEffect(() => {
    if (match?.pathname) {
      if (currentSession?.state === "claimed") {
        startReviewing();
      }
    }
  }, [match?.pathname, currentSession]);

  if (errorCode !== null) {
    return (
      <Container>
        <ErrorBox collapsed={collapsed} errorCode={errorCode} />
      </Container>
    );
  }

  return (
    <Container>
      <DisconnectModal
        open={sessionState === "canceled"}
        name={patientName}
        onMessage={() => {
          navigate(`/messages/patients/${patientId}`);
          requestStatus();
        }}
        onClose={() => {
          requestStatus();
        }}
      />

      {isReady && !patientId && !sessionState && (
        <Toggle
          collapsed={collapsed}
          checked={available}
          toggleOn={() => {
            toggleAvailability();
            AnalyticsService.track(AnalyticsEvents.DROP_IN_CALLS.TOGGLE_AVAILABLE_ON_CLICKED, { placement: "sidebar" });
          }}
          toggleOff={() => {
            toggleAvailability();
            AnalyticsService.track(AnalyticsEvents.DROP_IN_CALLS.TOGGLE_AVAILABLE_OFF_CLICKED, {
              placement: "sidebar",
            });
          }}
        />
      )}

      {isReady && patientId && sessionState === "claimed" && (
        <Assigned
          collapsed={collapsed}
          onClick={() => {
            AnalyticsService.track(AnalyticsEvents.DROP_IN_CALLS.START_REVIEW_CLICKED, { placement: "sidebar" });
            navigate(`/patients/${patientId}`);
          }}
          time={timerTime}
          name={patientName}
        />
      )}

      {isReady && patientId && sessionState === "in_review" && (
        <InReview
          collapsed={collapsed}
          patientId={patientId}
          onClick={() => {
            finishReviewing();
            AnalyticsService.track(AnalyticsEvents.DROP_IN_CALLS.FINISH_REVIEW_CLICKED, { placement: "sidebar" });
          }}
          time={expiryTime}
          name={patientName}
        />
      )}

      {askForFeedback && <DropInCallFeedbackPopup setOpen={setAskForFeedback} patientId={patientId} />}
    </Container>
  );
};

const ErrorBox = ({ collapsed, errorCode }: { collapsed: boolean; errorCode: number }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Pane
      spacing={0.5}
      alignItems="center"
      direction={collapsed ? "column" : "row"}
      py={1}
      sx={{ background: theme.colors.redesign.r100, padding: collapsed ? [0.5, 1] : undefined }}
    >
      <Typography variant="subtitle2" color={theme.colors.white}>
        {collapsed ? t("errors.error") : t("errors.generic")}
      </Typography>

      <Tooltip title={t("drop_in_calls.error", { errorcode: errorCode })} placement="bottom" arrow>
        <FontAwesomeIcon icon={faCircleQuestion} style={{ color: theme.colors.white, fontSize: 14 }} />
      </Tooltip>
    </Pane>
  );
};

const Assigned = ({
  collapsed,
  onClick,
  time,
  name,
}: {
  collapsed: boolean;
  onClick: () => void;
  time: string;
  name: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return collapsed ? (
    <MiniButton onClick={onClick}>
      <div>
        <div>{time}</div>
        <MiniButtonLabel>{t("drop_in_calls.pane.review_cta_small")}</MiniButtonLabel>
      </div>
    </MiniButton>
  ) : (
    <BigButton onClick={onClick}>
      <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
        <AssignedTopRow>
          <ClockColorWrapper /> {time}
        </AssignedTopRow>
        <Typography variant="label" color={theme.colors.white}>
          {t("drop_in_calls.pane.review_waiting", { name })}
        </Typography>
      </Box>

      <FontAwesomeIcon icon={faChevronRight} color={theme.colors.white} />
    </BigButton>
  );
};

const InReview = ({
  collapsed,
  onClick,
  patientId,
  time,
  name,
}: {
  collapsed: boolean;
  onClick: () => void;
  patientId: number;
  time: string;
  name: string;
}) => {
  const { t } = useTranslation();
  return collapsed ? (
    <MiniButton onClick={onClick}>
      <div>
        <CheckmarkIcon />
        <MiniButtonLabel>{t("drop_in_calls.pane.finish_cta_small")}</MiniButtonLabel>
      </div>
    </MiniButton>
  ) : (
    <Pane spacing={1} direction="column" py={1}>
      <Stack spacing={0.5} alignItems="center" direction="row">
        <Typography variant="subtitle2">
          <FontAwesomeIcon icon={faPhone} style={{ color: BLUE[100], fontSize: 16, marginRight: 2 }} />
          {name}
        </Typography>

        <Tooltip
          title={
            <Trans
              i18nKey="drop_in_calls.pane.expected"
              values={{ time, name }}
              components={[<Link to={`/patients/${patientId}`} />]}
            />
          }
          placement="bottom"
          arrow
        >
          <FontAwesomeIcon icon={faCircleQuestion} style={{ color: DARK_BLUE[60], fontSize: 14 }} />
        </Tooltip>
      </Stack>

      <Stack spacing={1}>
        <BaseButton onClick={onClick} size="small" fullWidth>
          {t("drop_in_calls.pane.finish_cta")}
        </BaseButton>
      </Stack>
    </Pane>
  );
};

const Toggle = ({
  collapsed,
  toggleOn,
  toggleOff,
  checked,
}: {
  collapsed: boolean;
  toggleOn: () => void;
  toggleOff: () => void;
  checked: boolean;
}) => {
  const { t } = useTranslation();
  const { askForDesktopNotificationPermission } = useNotificationsContext();
  if (collapsed) {
    return (
      <MiniPane alignItems="center">
        <MUISwitch
          id="enable-drop-in-call-switch"
          type="checkbox"
          checked={checked}
          onChange={() => {
            if (checked) {
              toggleOff();
            } else {
              toggleOn();
              askForDesktopNotificationPermission();
            }
          }}
        />
        <Collapse in={checked}>
          <DotsAnimationSmall>
            <Player speed={0.5} autoplay loop src={json} />
          </DotsAnimationSmall>
        </Collapse>
      </MiniPane>
    );
  }

  return (
    <Pane>
      <Stack spacing={1} alignItems="center" justifyContent="space-between" direction="row">
        <Stack spacing={0.5} alignItems="center" direction="row" overflow="hidden">
          <Typography variant="subtitle2" whiteSpace="nowrap">
            {t("drop_in_calls.pane.header_small")}
          </Typography>

          <Tooltip title={t("drop_in_calls.pane.instructions")} placement="bottom" arrow>
            <FontAwesomeIcon icon={faCircleQuestion} style={{ color: DARK_BLUE[60], fontSize: 16 }} />
          </Tooltip>
        </Stack>

        <MUISwitch
          id="enable-drop-in-call-switch"
          type="checkbox"
          checked={checked}
          onChange={() => {
            if (checked) {
              toggleOff();
            } else {
              toggleOn();
              askForDesktopNotificationPermission();
            }
          }}
        />
      </Stack>

      <Collapse in={checked}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mt={0.5}>
          <DotsAnimation>
            <Player speed={0.5} autoplay loop src={jsonSmall} />
          </DotsAnimation>
          <Typography variant="label" color={DARK_BLUE[70]} textAlign="right" whiteSpace="nowrap">
            {t("drop_in_calls.pane.waiting_for_patient")}
          </Typography>
        </Box>
      </Collapse>
    </Pane>
  );
};

const BigButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 12px;
  gap: 8px;
  cursor: pointer;
  background-color: ${props => props.theme.colors.redesign.b100};

  &:hover {
    background-color: ${props => props.theme.colors.redesign.b80};
  }
`;

const Container = styled.div`
  margin-top: 12px;
  margin-bottom: 12px;
`;

const Pane = styled(Stack)`
  background-color: ${props => props.theme.colors.redesign.db5};
  padding: 12px 16px;
  border-radius: 12px;
`;

const MiniPane = styled(Pane)`
  padding: 8px 4px;
`;

const MiniButton = styled.div`
  padding: 8px 4px;
  margin: 2px;
  text-align: center;
  background-color: ${props => props.theme.colors.redesign.b100};
  color: white;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  box-shadow: 1px 2px 5px rgba(0, 0, 0, 0.3);
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MiniButtonLabel = styled.div`
  font-size: 10px;
`;

const AssignedTopRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: ${props => props.theme.colors.white};
`;

const ClockColorWrapper = styled(ClockColor)`
  width: 16px;
  height: 16px;
  color: ${props => props.theme.colors.white};
  margin-right: 4px;
  margin-top: -1px;
`;

const DotsAnimation = styled.div`
  width: 38px;
`;

const DotsAnimationSmall = styled.div`
  width: 38px;
  margin: ${props => props.theme.spacing.S_5} auto 0 auto;
`;
